import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Sparkles, X } from "lucide-react";
import { cardBase, inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

import {
  createSession,
  sendMessage,
  suggestFollowups,
  type ChatScope,
  type WidgetSpec,
} from "@/lib/api/agent";
import { useRole } from "@/lib/roles";
import { roleAgentSuggestions } from "@/lib/role-content";
import type { ChatContextPack } from "@/lib/chat-context-packs";
import {
  ensureChatSession,
  type SessionSlot,
} from "@/components/chat/ensure-chat-session";
import { KairosLogo } from "@/components/control-tower/KairosLogo";
import { ChatMessageMarkdown } from "@/components/chat/ChatMessageMarkdown";
import { WidgetRenderer } from "@/components/chat/WidgetRenderer";

interface ChatEntry {
  role: "user" | "agent";
  content: string;
  widgets: WidgetSpec[];
}

interface Props {
  scope: ChatScope;
  className?: string;
  onClose?: () => void;
  buildingId?: string;
  // Per-lens context pack override — omit for the default,
  // scope-derived header + role suggestions used by the general/portfolio entry points.
  contextPack?: ChatContextPack;
  // When false (drawer closed but still mounted), abort any in-flight SSE turn.
  active?: boolean;
}

function quick_reply_label(
  content: string,
): { label: string; message: string } | null {
  if (/ehdott\w+ muutoksi|aloitan ehdott|haluatko.*aloitan/i.test(content))
    return { label: "Ehdota muutoksia", message: "Ehdota muutoksia" };
  if (/yhdist\w+ vyöhykke|haluatko.*yhdist/i.test(content))
    return {
      label: "Yhdistä vyöhykkeisiin",
      message: "Yhdistä puuttuvat laitteet vyöhykkeisiin",
    };
  return null;
}

function wait_status_label(status: string | null): string {
  if (status === "retrieving") return "Haetaan tietoja…";
  return "Mietitään…";
}

export function SmartChat({
  scope,
  className,
  onClose,
  buildingId,
  contextPack,
  active = true,
}: Props) {
  const { role } = useRole();
  const suggestions = contextPack?.suggestions ?? roleAgentSuggestions[role];
  const scoped_building_id =
    buildingId ??
    (scope.level === "building" && "context_id" in scope
      ? scope.context_id
      : undefined);

  const [session_id, set_session_id] = useState<string | null>(null);
  const [messages, set_messages] = useState<ChatEntry[]>([]);
  const [input, set_input] = useState("");
  const [streaming, set_streaming] = useState(false);
  const [wait_status, set_wait_status] = useState<string | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const [dynamic_suggestions, set_dynamic_suggestions] = useState<
    string[] | null
  >(null);
  const [suggesting, set_suggesting] = useState(false);
  const messages_ref = useRef<ChatEntry[]>([]);
  // One create per SmartChat instance, deduped across concurrent first-sends.
  const session_slot = useRef<SessionSlot>({ id: null, inflight: null });
  const abort_ref = useRef<AbortController | null>(null);

  const scroll_ref = useRef<HTMLDivElement>(null);

  // Keep ref in sync so refresh_suggestions always reads fresh messages
  useEffect(() => {
    messages_ref.current = messages;
  }, [messages]);

  // Scroll to bottom on new content
  useEffect(() => {
    if (scroll_ref.current) {
      scroll_ref.current.scrollTop = scroll_ref.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (active) return;
    abort_ref.current?.abort();
  }, [active]);

  useEffect(() => {
    return () => abort_ref.current?.abort();
  }, []);

  // On-demand only: a full LLM generation, fired when the user explicitly
  // asks for fresh suggestions — never automatically after each turn (that
  // added an extra generation per message and raised overload-backoff risk).
  const refresh_suggestions = useCallback(async () => {
    const msg_list = messages_ref.current
      .filter((m) => m.content)
      .map((m) => ({ role: m.role, content: m.content }));
    if (msg_list.length === 0) return;
    set_suggesting(true);
    try {
      const result = await suggestFollowups(msg_list, scope);
      if (result.suggestions.length > 0)
        set_dynamic_suggestions(result.suggestions);
    } catch {
      // silently fall back to static suggestions
    } finally {
      set_suggesting(false);
    }
  }, [scope]);

  async function ensure_session(): Promise<string> {
    return ensureChatSession(session_slot.current, () =>
      createSession(scope).then((s) => {
        set_session_id(s.id);
        return s;
      }),
    );
  }

  async function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    set_input("");
    set_error(null);

    let sid: string;
    try {
      sid = await ensure_session();
    } catch {
      set_error("Yhteyttä ei voitu muodostaa. Yritä uudelleen.");
      return;
    }

    // User bubble at messages.length, agent bubble at messages.length + 1.
    // Capture before any state updates (stale closure is intentional here).
    const agent_idx = messages.length + 1;
    set_messages((prev) => [
      ...prev,
      { role: "user" as const, content: trimmed, widgets: [] },
      { role: "agent" as const, content: "", widgets: [] },
    ]);

    abort_ref.current?.abort();
    const abort = new AbortController();
    abort_ref.current = abort;

    set_streaming(true);
    set_wait_status("thinking");
    try {
      await sendMessage(
        sid,
        trimmed,
        (delta) => {
          set_messages((prev) => {
            const updated = [...prev];
            const target = updated[agent_idx];
            if (target?.role === "agent") {
              updated[agent_idx] = {
                ...target,
                content: target.content + delta,
              };
            }
            return updated;
          });
        },
        (widget) => {
          set_messages((prev) => {
            const updated = [...prev];
            const target = updated[agent_idx];
            if (target?.role === "agent") {
              updated[agent_idx] = {
                ...target,
                widgets: [...target.widgets, widget],
              };
            }
            return updated;
          });
        },
        abort.signal,
        set_wait_status,
      );
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      set_error(e instanceof Error ? e.message : "Virhe");
      set_messages((prev) => {
        const updated = [...prev];
        const target = updated[agent_idx];
        if (target?.role === "agent" && !target.content) {
          updated[agent_idx] = {
            ...target,
            content: "Jokin meni pieleen. Yritä uudelleen.",
          };
        }
        return updated;
      });
    } finally {
      set_streaming(false);
      set_wait_status(null);
    }
  }

  function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    void submit(input);
  }

  return (
    <aside
      className={cn(
        "w-full flex flex-col overflow-hidden",
        cardBase,
        className,
      )}
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        <KairosLogo size={26} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            {contextPack?.title ?? "Kairos"}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {contextPack?.subtitle ??
              (scope.level === "building"
                ? "Rakennustason kysymykset"
                : scope.level === "group"
                  ? "Salkkutason kysymykset"
                  : "Kiinteistökannan kysymykset")}
          </div>
        </div>
        <span className="text-[10px] text-success flex items-center gap-1">
          <span
            className={`h-1.5 w-1.5 rounded-full ${session_id ? "bg-success animate-pulse" : "bg-muted"}`}
          />
          {session_id ? "live" : ""}
        </span>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Sulje chat"
            className="ml-1 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-surface transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      <div
        ref={scroll_ref}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm min-h-0"
      >
        {messages.length === 0 && (
          <p className="text-[12px] text-muted-foreground text-center pt-4">
            Kysy mitä tahansa kiinteistöistäsi. Kairos hakee reaaliaikadatan ja
            vastaa.
          </p>
        )}
        {messages.map((m, i) => {
          const is_last = i === messages.length - 1;
          const quick_reply =
            is_last &&
            m.role === "agent" &&
            !streaming &&
            m.content.includes("Haluatko")
              ? quick_reply_label(m.content)
              : null;
          return (
            <div
              key={i}
              className={m.role === "user" ? "flex justify-end" : ""}
            >
              <div
                className={
                  m.role === "user"
                    ? "max-w-[85%] rounded-2xl rounded-tr-sm bg-surface-elevated border border-border px-3 py-2 text-foreground"
                    : "max-w-[96%] rounded-2xl rounded-tl-sm bg-surface px-3 py-2.5 border border-border/60 text-foreground/90"
                }
              >
                {m.content &&
                  (m.role === "agent" ? (
                    <ChatMessageMarkdown content={m.content} />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {m.content}
                    </p>
                  ))}
                {m.role === "agent" && !m.content && streaming && is_last && (
                  <span className="inline-flex gap-1.5 items-center text-muted-foreground text-xs">
                    <span className="inline-flex gap-1 items-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
                    </span>
                    {wait_status_label(wait_status)}
                  </span>
                )}
                {m.widgets.map((w, wi) => (
                  <WidgetRenderer
                    key={wi}
                    widget={w}
                    buildingId={scoped_building_id}
                  />
                ))}
              </div>
              {quick_reply && (
                <div className="mt-1.5 flex gap-1.5">
                  <button
                    onClick={() => void submit(quick_reply.message)}
                    disabled={streaming}
                    className="text-[11px] text-primary border border-primary/30 bg-primary/5 rounded-full px-2.5 py-1 hover:bg-primary/10 hover:border-primary/50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {quick_reply.label}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p className="mx-4 mb-2 text-[11px] text-destructive border border-destructive/20 rounded-md px-3 py-1.5 bg-destructive/5 shrink-0">
          {error}
        </p>
      )}

      <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
        {(dynamic_suggestions ?? suggestions).map((s) => (
          <button
            key={s}
            onClick={() => void submit(s)}
            disabled={streaming}
            className="text-[11px] text-muted-foreground hover:text-foreground border border-border bg-surface rounded-full px-2.5 py-1 hover:border-primary/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {s}
          </button>
        ))}
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => void refresh_suggestions()}
            disabled={streaming || suggesting}
            aria-label="Ehdota lisää kysymyksiä"
            className="inline-flex items-center gap-1 text-[11px] text-primary border border-primary/30 bg-primary/5 rounded-full px-2.5 py-1 hover:bg-primary/10 hover:border-primary/50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="h-3 w-3" />
            {suggesting ? "Ehdotetaan…" : "Ehdota lisää"}
          </button>
        )}
      </div>

      <form
        onSubmit={handle_submit}
        className="p-3 border-t border-border bg-surface-elevated/40 shrink-0"
      >
        <div className={`relative ${inputGlowWrap}`}>
          <div
            className={`flex items-center gap-2 ${inputGlowInner} px-3 py-2.5`}
          >
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <input
              value={input}
              onChange={(e) => set_input(e.target.value)}
              placeholder="Kysy mitä tahansa kiinteistöistäsi…"
              disabled={streaming}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground/60 placeholder:font-medium disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              className="h-8 w-8 grid place-items-center rounded-md bg-primary text-primary-foreground hover:opacity-90 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Lähetä"
            >
              {streaming ? (
                <svg
                  className="h-3.5 w-3.5 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground text-center">
          Kairos vastaa reaaliaikadatasta
        </div>
      </form>
    </aside>
  );
}
